import { hasProductPackages } from '@/lib/package-utils'
import { useDatePicker } from '@/components/MyDatePicker'
import dayjs from 'dayjs'
import {
  useReadableCost,
  useReadableEr,
  useReadableRevenue,
} from '@/hooks/auth'
import { useMemo } from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { getDateRange } from '@/lib/date-utils'
import { getGlobalLatestStartDate } from '@/lib/dr-utils'
import { useContrastModeStore } from '@/components/roi/ContrastModeSelector'
import ContrastModeSelector from '@/components/roi/ContrastModeSelector'
import { getContrastDateRange, getContrastDate } from '@/lib/contrast-date-utils'

import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

import {
  costColumn,
  costsDetailColumn,
  cpiColumn,
  cpnColumn,
  dauColumn,
  dnuColumn,
  erColumn,
  installsColumn,
  ltvColumn,
  originalColumn,
  profitColumn,
  revenueColumn,
  revenueDetailColumn,
  rpaColumn,
  rpnColumn,
  day0RoasColumn,
  day3RoasColumn,
} from './columns'

import { ChartCodeViewer } from '@/components/zthd/chart-code-viewer'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import Loader from '@/components/zthd/Loader'
import {
  useColumnFilters,
  VSWeekSelector,
} from '@/components/data-table-selectors'
import { useCountry, useCountry2 } from '@/components/DropdownCountry'
import { usePid2 } from '@/components/DropdownPid'
import { usePackageName } from '@/components/DropdownPackage'
import RoiChart from './roi-chart'
import columnDate from '@/components/roi/column-date'
import { useReadableRoas } from '@/hooks/auth'
import { ROI_COST_SOURCES } from '@/constants/roi-cost-source'
import { ROI_REVENUE_SOURCES } from '@/constants/roi-revenue-source'
import { useNoteDates } from '../use-note-dates'

// Columns hidden by default (C/N, R/N, R/A and per-source Install / CPI under
// Costs); toggle via View
const DEFAULT_COLUMN_VISIBILITY = {
  cpn: false,
  rpn: false,
  rpa: false,
  ...Object.fromEntries(
    ROI_COST_SOURCES.flatMap((source) => [
      [`costs-${source}-install`, false],
      [`costs-${source}-cpi`, false],
    ]),
  ),
}

const useColumnVisibilityStore = createColumnVisibilityStore(
  DEFAULT_COLUMN_VISIBILITY,
  'roi',
  { mergeInit: true },
)

const DataTable = () => {
  const pids = usePid2((state) => state.pids)
  const pid = pids.join(',') === '' ? 'total' : pids.join(',')
  const country = useCountry((state) => state.value)
  const countries = useCountry2((state) => state.countries)
  const packageName = usePackageName((state) => state.value)

  const readableRevenue = useReadableRevenue(pid, country)
  const readableCost = useReadableCost(pid, country)
  const readableEr = useReadableEr(pid, country)
  const readableRoas = useReadableRoas(pid, country)
  const { data: baselins } = useSWR(['/ers/baselines'])
  const erBaseLine = baselins?.[country]?.[pid] ?? 0.35
  const { data: drBaselines } = useSWR(['/drs/baselines'])
  const { data: costBaselines } = useSWR(['/costs/baselines'])
  const { data: profitBaselines } = useSWR(['/profits/baselines'])

  // 获取全局最新的 baseline 配置日期
  const latestBaselineDate = useMemo(() => {
    if (!drBaselines) return ''
    const day0Date = getGlobalLatestStartDate(drBaselines, 0)
    const day3Date = getGlobalLatestStartDate(drBaselines, 3)
    // D0 和 D3 通常一样，取较大的那个，或任意一个非空值
    return day0Date || day3Date || ''
  }, [drBaselines])

  const startDate = useDatePicker((state) => state.startDate)
  const endDate = useDatePicker((state) => state.endDate)
  const noteDates = useNoteDates(startDate, endDate)
  const { mutate } = useSWRConfig()

  // 获取对比模式状态
  const contrastEnabled = useContrastModeStore((state) => state.enabled)
  const contrastType = useContrastModeStore((state) => state.type)
  const customYear = useContrastModeStore((state) => state.customYear)
  const customMonth = useContrastModeStore((state) => state.customMonth)

  // 计算对比日期范围
  const contrastDateRange = useMemo(() => {
    if (!contrastEnabled || !contrastType) {
      return { startDate: null, endDate: null }
    }
    return getContrastDateRange(startDate, endDate, contrastType, customYear, customMonth)
  }, [contrastEnabled, contrastType, startDate, endDate, customYear, customMonth])

  const queryOpt = useMemo(
    () => ({ startDate, endDate, pid: pids, country, packageName, countries }),
    [startDate, endDate, pids, country, packageName, countries],
  )

  // 对比数据查询参数
  const contrastQueryOpt = useMemo(() => {
    if (!contrastEnabled || !contrastDateRange.startDate || !contrastDateRange.endDate) {
      return null
    }
    return {
      startDate: contrastDateRange.startDate,
      endDate: contrastDateRange.endDate,
      pid: pids,
      country,
      packageName,
      countries,
    }
  }, [contrastEnabled, contrastDateRange, pids, country, packageName, countries])

  const { data: revenues, isValidating: isFetchRevenueing } = useSWR([
    '/revenues',
    queryOpt,
  ])

  const { data: costs, isValidating: isFetchCosting } = useSWR([
    '/costs',
    queryOpt,
  ])

  const { data: dnus, isValidating: isFetchDnuing } = useSWR([
    '/userActives',
    queryOpt,
  ])

  const { data: day0Roas, isValidating: isFetchDay0Roas } = useSWR(readableRoas ? [
    '/drs',
    { ...queryOpt, day_n: 0 },
  ] : null)

  const { data: day3Roas, isValidating: isFetchDay3Roas } = useSWR(readableRoas ? [
    '/drs',
    { ...queryOpt, day_n: 3 },
  ] : null)
  const { data: ers, isValidating: isFetchErsing } = useSWR(readableEr ? [
    '/ers',
    queryOpt,
  ] : null)

  // 对比数据获取
  const { data: contrastRevenues, isValidating: isFetchContrastRevenueing } = useSWR(
    contrastQueryOpt ? ['/revenues', contrastQueryOpt] : null,
  )

  const { data: contrastCosts, isValidating: isFetchContrastCosting } = useSWR(
    contrastQueryOpt ? ['/costs', contrastQueryOpt] : null,
  )

  const { data: contrastDnus, isValidating: isFetchContrastDnuing } = useSWR(
    contrastQueryOpt ? ['/userActives', contrastQueryOpt] : null,
  )

  const { data: contrastDay0Roas, isValidating: isFetchContrastDay0Roas } = useSWR(
    contrastQueryOpt && readableRoas ? ['/drs', { ...contrastQueryOpt, day_n: 0 }] : null,
  )

  const { data: contrastDay3Roas, isValidating: isFetchContrastDay3Roas } = useSWR(
    contrastQueryOpt && readableRoas ? ['/drs', { ...contrastQueryOpt, day_n: 3 }] : null,
  )

  const { data: contrastErs, isValidating: isFetchContrastErsing } = useSWR(
    contrastQueryOpt && readableEr ? ['/ers', contrastQueryOpt] : null,
  )

  const handleRefrech = () => {
    const paths = ['/revenues', '/costs', '/userActives', '/ers', '/drs', '/notes', '/troas/notes', '/troas/notes/dates']
    // Revalidate without clearing cached data, including note summaries and open details.
    mutate((key) => Array.isArray(key) && paths.includes(key[0]))
  }

  const pendingMetrics = [
    ['Revenue', revenues, isFetchRevenueing],
    ['Cost', costs, isFetchCosting],
    ['Users', dnus, isFetchDnuing],
    ['D0 ROAS', day0Roas, isFetchDay0Roas],
    ['D3 ROAS', day3Roas, isFetchDay3Roas],
    ['ER', ers, isFetchErsing],
    ['Comparison revenue', contrastRevenues, isFetchContrastRevenueing],
    ['Comparison cost', contrastCosts, isFetchContrastCosting],
    ['Comparison users', contrastDnus, isFetchContrastDnuing],
    ['Comparison D0 ROAS', contrastDay0Roas, isFetchContrastDay0Roas],
    ['Comparison D3 ROAS', contrastDay3Roas, isFetchContrastDay3Roas],
    ['Comparison ER', contrastErs, isFetchContrastErsing],
  ].filter(([, , validating]) => validating)
  const isInitialLoading = pendingMetrics.some(([, value]) => value === undefined)
  const columnVisibility = useColumnVisibilityStore(
    (state) => state.columnVisibility,
  )
  const setColumnVisibility = useColumnVisibilityStore(
    (state) => state.setColumnVisibility,
  )

  const columnFilters = useColumnFilters((state) => state.columnFilters)

  const data = useMemo(() => {
    const dateRange = getDateRange(dayjs(startDate), dayjs(endDate))
    return dateRange.map((date) => {
      // 处理当前数据
      const _revenues = revenues?.[date]
      if (_revenues) {
        const _otherKeys = []
        Object.keys(_revenues).forEach((key) => {
          if (!ROI_REVENUE_SOURCES.includes(key) && key !== 'total') {
            _revenues.other = (_revenues.other || 0) + _revenues[key]
            _otherKeys.push(key)
          }
        })
        _otherKeys.forEach((key) => {
          delete _revenues[key]
        })
      }
      const revenue = _revenues?.total || 0
      const _costs = costs?.[date]
      const cost = _costs?.total?.costs || 0
      const installs = _costs?.total?.installs || 0
      const ua = dnus?.[date]
      const profit = (revenue || 0) - (cost || 0)

      // 处理对比数据
      let contrast = null
      let diff = {}
      let diffPercent = {}

      if (contrastEnabled && contrastType) {
        const contrastDate = getContrastDate(date, contrastType, customYear, customMonth)
        if (contrastDate) {
          const _contrastRevenues = contrastRevenues?.[contrastDate]
          let processedContrastRevenues = _contrastRevenues
          if (_contrastRevenues) {
            const _otherKeys = []
            Object.keys(_contrastRevenues).forEach((key) => {
              if (!ROI_REVENUE_SOURCES.includes(key) && key !== 'total') {
                _contrastRevenues.other = (_contrastRevenues.other || 0) + _contrastRevenues[key]
                _otherKeys.push(key)
              }
            })
            _otherKeys.forEach((key) => {
              delete _contrastRevenues[key]
            })
            processedContrastRevenues = _contrastRevenues
          }
          const contrastRevenue = processedContrastRevenues?.total || 0
          const _contrastCosts = contrastCosts?.[contrastDate]
          const contrastCost = _contrastCosts?.total?.costs || 0
          const contrastInstalls = _contrastCosts?.total?.installs || 0
          const contrastUa = contrastDnus?.[contrastDate]
          const contrastProfit = contrastRevenue - contrastCost

          contrast = {
            date: contrastDate,
            revenues: processedContrastRevenues,
            revenue: contrastRevenue,
            costs: _contrastCosts,
            cost: contrastCost,
            profit: contrastProfit,
            installs: contrastInstalls,
            dnu: contrastUa?.dnu,
            dau: contrastUa?.dau,
            original: (contrastUa?.dnu || 0) - contrastInstalls,
            er: contrastErs?.[contrastDate],
            day0Roas: contrastDay0Roas?.[contrastDate],
            day3Roas: contrastDay3Roas?.[contrastDate],
          }

          // 计算差异值和百分比
          const calculateDiff = (current, contrast) => {
            const currentVal = current ?? 0
            const contrastVal = contrast ?? 0
            if (current == null && contrast == null) return null
            return currentVal - contrastVal
          }

          const calculateDiffPercent = (current, contrast) => {
            if (current == null && contrast == null) return null
            const currentVal = current ?? 0
            const contrastVal = contrast ?? 0
            if (contrastVal === 0) return null
            return ((currentVal - contrastVal) / contrastVal) * 100
          }

          diff = {
            revenue: calculateDiff(revenue, contrastRevenue),
            cost: calculateDiff(cost, contrastCost),
            profit: calculateDiff(profit, contrastProfit),
            installs: calculateDiff(installs, contrastInstalls),
            dnu: calculateDiff(ua?.dnu, contrastUa?.dnu),
            dau: calculateDiff(ua?.dau, contrastUa?.dau),
            original: calculateDiff((ua?.dnu || 0) - (installs || 0), (contrastUa?.dnu || 0) - contrastInstalls),
            er: calculateDiff(ers?.[date], contrastErs?.[contrastDate]),
            day0Roas: calculateDiff(day0Roas?.[date]?.avg_roas, contrastDay0Roas?.[contrastDate]?.avg_roas),
            day3Roas: calculateDiff(day3Roas?.[date]?.avg_roas, contrastDay3Roas?.[contrastDate]?.avg_roas),
          }

          diffPercent = {
            revenue: calculateDiffPercent(revenue, contrastRevenue),
            cost: calculateDiffPercent(cost, contrastCost),
            profit: calculateDiffPercent(profit, contrastProfit),
            installs: calculateDiffPercent(installs, contrastInstalls),
            dnu: calculateDiffPercent(ua?.dnu, contrastUa?.dnu),
            dau: calculateDiffPercent(ua?.dau, contrastUa?.dau),
            original: calculateDiffPercent((ua?.dnu || 0) - (installs || 0), (contrastUa?.dnu || 0) - contrastInstalls),
            er: calculateDiffPercent(ers?.[date], contrastErs?.[contrastDate]),
            day0Roas: calculateDiffPercent(day0Roas?.[date]?.avg_roas, contrastDay0Roas?.[contrastDate]?.avg_roas),
            day3Roas: calculateDiffPercent(day3Roas?.[date]?.avg_roas, contrastDay3Roas?.[contrastDate]?.avg_roas),
          }
        }
      }

      return {
        date,
        revenues: _revenues,
        revenue,
        costs: _costs,
        cost,
        profit,
        installs,
        dnu: ua?.dnu,
        dau: ua?.dau,
        original: (ua?.dnu || 0) - (installs || 0),
        er: ers?.[date],
        day0Roas: day0Roas?.[date],
        day3Roas: day3Roas?.[date],
        contrast,
        diff,
        diffPercent,
      }
    })
  }, [
    startDate,
    endDate,
    revenues,
    costs,
    dnus,
    ers,
    day0Roas,
    day3Roas,
    contrastEnabled,
    contrastType,
    customYear,
    customMonth,
    contrastRevenues,
    contrastCosts,
    contrastDnus,
    contrastErs,
    contrastDay0Roas,
    contrastDay3Roas,
  ])

  const columns = useMemo(() => {
    const pidForBaseline = pids.length === 1 ? pids[0] : null
    // 将产品子包传入标准查询，避免 Bus 继承原版标准。
    const packageNameForBaseline = hasProductPackages(pidForBaseline) && packageName !== 'total' ? packageName : null
    const rows = [
      columnDate(pid, country, drBaselines, pidForBaseline, costBaselines, profitBaselines, packageNameForBaseline),
      dnuColumn,
      dauColumn,
    ]

    if (readableCost) {
      rows.push(originalColumn)
      rows.push(installsColumn)
    }

    if (readableRevenue) {
      rows.push(revenueColumn)
    }
    if (readableCost) {
      rows.push(costColumn(costBaselines, country, pidForBaseline))
    }

    if (readableRevenue && readableCost) {
      rows.push(profitColumn(profitBaselines, country, pidForBaseline))
    }

    if (readableEr) {
      rows.push(erColumn(erBaseLine))
    }
    if (readableRoas) {
      rows.push(day0RoasColumn(drBaselines, country, pidForBaseline, packageNameForBaseline))
      rows.push(day3RoasColumn(drBaselines, country, pidForBaseline, packageNameForBaseline))
    }
    if (readableCost) {
      rows.push(cpnColumn)
      rows.push(cpiColumn)
    }

    if (readableRevenue) {
      rows.push(rpnColumn)
      rows.push(rpaColumn)
    }

    if (readableRevenue && readableCost) {
      rows.push(ltvColumn)
    }

    if (readableCost) {
      rows.push({ id: 'space-end', isPlaceholder: true, enableHiding: false })
      rows.push(costsDetailColumn)
    }
    if (readableRevenue) {
      rows.push({ id: 'space-end2', isPlaceholder: true, enableHiding: false })
      rows.push(revenueDetailColumn)
    }
    return rows
  }, [
    erBaseLine,
    drBaselines,
    costBaselines,
    profitBaselines,
    country,
    pid,
    pids,
    packageName,
    readableRevenue,
    readableCost,
    readableEr,
    readableRoas,
  ])

  const table = useReactTable({
    data,
    columns,
    meta: { noteDates: noteDates.dates },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  // 生成对比模式说明
  const contrastDescription = useMemo(() => {
    if (!contrastEnabled || !contrastType) return null
    
    const { startDate: cs, endDate: ce } = contrastDateRange
    if (!cs || !ce) return null
    
    const currentStart = dayjs(String(startDate), 'YYYYMMDD').format('YYYY-MM-DD')
    const currentEnd = dayjs(String(endDate), 'YYYYMMDD').format('YYYY-MM-DD')
    const contrastStart = dayjs(String(cs), 'YYYYMMDD').format('YYYY-MM-DD')
    const contrastEnd = dayjs(String(ce), 'YYYYMMDD').format('YYYY-MM-DD')
    
    let typeText = ''
    switch (contrastType) {
      case 'yoy':
        typeText = '去年同期'
        break
      case 'mom':
        typeText = '上月同期'
        break
      case 'custom':
        typeText = '自定义对比'
        break
      default:
        typeText = '对比'
    }
    
    return (
      <ContrastDescription
        currentStart={currentStart}
        currentEnd={currentEnd}
        contrastStart={contrastStart}
        contrastEnd={contrastEnd}
        typeText={typeText}
      />
    )
  }, [contrastEnabled, contrastType, contrastDateRange, startDate, endDate])

  // 获取对比模式显示选项的状态和设置函数
  const showContrastValue = useContrastModeStore((state) => state.showContrastValue)
  const showDiff = useContrastModeStore((state) => state.showDiff)
  const showDiffPercent = useContrastModeStore((state) => state.showDiffPercent)
  const setShowContrastValue = useContrastModeStore((state) => state.setShowContrastValue)
  const setShowDiff = useContrastModeStore((state) => state.setShowDiff)
  const setShowDiffPercent = useContrastModeStore((state) => state.setShowDiffPercent)

  return (
    <>
      <div className="flex items-center gap-4 py-4 flex-wrap sm:gap-2 sm:py-2 xs:py-1 xs:gap-1">
        {/* 对比模式选择器放在第二行的第一个位置 */}
        <ContrastModeSelector />
        {/* 对比选项框、周几选项框、刷新按钮、Chart按钮，确保在同一行 */}
        <div className="flex items-center gap-4 flex-nowrap">
          {/* 对比模式显示选项，仅在对比模式启用时显示 */}
          {contrastEnabled && (
            <div className="flex items-center gap-3 text-xs flex-nowrap">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-contrast-value"
                  checked={showContrastValue}
                  onCheckedChange={setShowContrastValue}
                />
                <Label htmlFor="show-contrast-value" className="cursor-pointer">
                  原始值
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-diff"
                  checked={showDiff}
                  onCheckedChange={setShowDiff}
                />
                <Label htmlFor="show-diff" className="cursor-pointer">
                  差异值
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-diff-percent"
                  checked={showDiffPercent}
                  onCheckedChange={setShowDiffPercent}
                />
                <Label htmlFor="show-diff-percent" className="cursor-pointer">
                  差异比例
                </Label>
              </div>
            </div>
          )}
          <VSWeekSelector />
          <Button onClick={handleRefrech} variant="outline">
            refresh
          </Button>
          <ChartCodeViewer>
            <RoiChart data={data} />
          </ChartCodeViewer>
        </div>
        {pendingMetrics.length > 0 && (
          <Loader>
            {isInitialLoading ? 'Loading' : 'Refreshing'}: {pendingMetrics.map(([label]) => label).join(', ')}...
          </Loader>
        )}
        {noteDates.isLoading && <span className="text-sm text-muted-foreground">Loading note markers...</span>}
        {noteDates.error && <span role="alert" className="text-sm text-destructive">Note markers failed to load. Please refresh.</span>}
        {!noteDates.error && !noteDates.ledgerAvailable && <span role="status" className="text-sm text-muted-foreground">Platform note markers are unavailable.</span>}
        <div className="text-destructive text-nowrap xs:text-xs xs:text-wrap lg:text-base">
          er&roas base line
          值只在具体的产品下是准确的;多个pid,或者全选时,等于35%&50%.
          Roas取的是total{latestBaselineDate && `(${latestBaselineDate})`}.
        </div>
        <DataTableViewOptions table={table} />
      </div>
      {contrastDescription}
      {/* <>{JSON.stringify(data[0])}</> */}
      <DataTableWithBorder table={table} />
    </>
  )
}

// 对比模式说明组件
const ContrastDescription = ({ currentStart, currentEnd, contrastStart, contrastEnd, typeText }) => {
  return (
    <div className="text-sm text-muted-foreground bg-blue-50 border border-blue-200 rounded p-2 mb-2">
      <div className="font-medium text-blue-900 mb-1">对比模式说明：</div>
      <div className="text-blue-800">
        <span className="font-medium">当前数据：</span> {currentStart} 至 {currentEnd}
      </div>
      <div className="text-blue-800">
        <span className="font-medium">{typeText}：</span> {contrastStart} 至 {contrastEnd}
      </div>
      <div className="text-blue-800 mt-2 text-xs">
        <span className="font-medium">说明：</span> 表格中每个单元格下方显示的是与对比期的差异值（↑表示增长，↓表示下降）和变化百分比。绿色表示增长，红色表示下降。
      </div>
    </div>
  )
}

export default DataTable
